/**
 * Codificador GIF89a (§7.5).
 *
 * Escrito à mão em vez de trazer uma biblioteca: o jogo tem duas dependências
 * de execução, `react` e `react-dom`, e o que se precisa aqui é um subconjunto
 * pequeno e estável de um formato de 1989 — tabela global de cores, quadros
 * parciais e laço infinito. O que não é trivial é a compressão LZW, e é ela
 * que o teste de ida e volta cobre, com um decodificador escrito à parte.
 *
 * O tabuleiro é feito de cores chapadas, então o GIF cai bem: as 256 cores da
 * paleta sobram, e o que consome de fato são as bordas do texto suavizado.
 */

export interface Cor {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/** Um quadro parcial: só o retângulo que mudou desde o anterior. */
export interface QuadroGif {
  readonly esquerda: number;
  readonly topo: number;
  readonly largura: number;
  readonly altura: number;
  /** Um índice da paleta por pixel, em linhas. */
  readonly indices: Uint8Array;
  /** Em centésimos de segundo — a unidade do formato. */
  readonly atrasoCs: number;
}

/* ── Paleta ─────────────────────────────────────────────────────────────── */

/**
 * Corte mediano: divide a caixa de cores pelo eixo mais espalhado, sempre pela
 * caixa que mais tem pixels, até chegar ao número de cores pedido.
 *
 * É o algoritmo clássico, e serve bem aqui porque a imagem tem poucas cores de
 * verdade e muitos tons intermediários vindos do texto suavizado: as caixas
 * grandes se partem onde há gente, e as cores chapadas sobrevivem intactas.
 */
export function paletaMediana(pixels: Uint8ClampedArray, maxCores: number): readonly Cor[] {
  const cores: Cor[] = [];
  for (let i = 0; i < pixels.length; i += 4) {
    cores.push({ r: pixels[i] ?? 0, g: pixels[i + 1] ?? 0, b: pixels[i + 2] ?? 0 });
  }
  if (cores.length === 0) return [{ r: 0, g: 0, b: 0 }];

  let caixas: Cor[][] = [cores];

  while (caixas.length < maxCores) {
    // A próxima a partir é a maior: dividir uma caixa quase vazia gastaria uma
    // entrada da paleta com uma cor que quase não aparece.
    let alvo = -1;
    let maior = 1;
    caixas.forEach((caixa, i) => {
      if (caixa.length > maior) {
        maior = caixa.length;
        alvo = i;
      }
    });
    if (alvo === -1) break;

    const caixa = caixas[alvo];
    if (caixa === undefined) break;

    const eixo = eixoMaisEspalhado(caixa);
    const ordenada = [...caixa].sort((a, b) => a[eixo] - b[eixo]);
    const meio = ordenada.length >> 1;
    if (meio === 0) break;

    caixas = [
      ...caixas.slice(0, alvo),
      ordenada.slice(0, meio),
      ordenada.slice(meio),
      ...caixas.slice(alvo + 1),
    ];
  }

  return caixas.filter((c) => c.length > 0).map(mediaDa);
}

function eixoMaisEspalhado(caixa: readonly Cor[]): 'r' | 'g' | 'b' {
  const faixa = (eixo: 'r' | 'g' | 'b') => {
    let min = 255;
    let max = 0;
    for (const cor of caixa) {
      if (cor[eixo] < min) min = cor[eixo];
      if (cor[eixo] > max) max = cor[eixo];
    }
    return max - min;
  };
  const fr = faixa('r');
  const fg = faixa('g');
  const fb = faixa('b');
  if (fr >= fg && fr >= fb) return 'r';
  return fg >= fb ? 'g' : 'b';
}

function mediaDa(caixa: readonly Cor[]): Cor {
  let r = 0;
  let g = 0;
  let b = 0;
  for (const cor of caixa) {
    r += cor.r;
    g += cor.g;
    b += cor.b;
  }
  const n = caixa.length;
  return { r: Math.round(r / n), g: Math.round(g / n), b: Math.round(b / n) };
}

/**
 * Troca cada pixel pelo índice da cor mais próxima.
 *
 * A busca é linear sobre a paleta, mas o cache a poupa quase toda: uma imagem
 * de cores chapadas repete o mesmo punhado de valores milhares de vezes.
 */
export function indexar(pixels: Uint8ClampedArray, paleta: readonly Cor[]): Uint8Array {
  const saida = new Uint8Array(pixels.length / 4);
  const cache = new Map<number, number>();

  for (let i = 0, p = 0; i < pixels.length; i += 4, p += 1) {
    const r = pixels[i] ?? 0;
    const g = pixels[i + 1] ?? 0;
    const b = pixels[i + 2] ?? 0;
    const chave = (r << 16) | (g << 8) | b;

    const guardado = cache.get(chave);
    if (guardado !== undefined) {
      saida[p] = guardado;
      continue;
    }

    let melhor = 0;
    let menorDistancia = Infinity;
    for (let k = 0; k < paleta.length; k += 1) {
      const cor = paleta[k];
      if (cor === undefined) continue;
      const dr = cor.r - r;
      const dg = cor.g - g;
      const db = cor.b - b;
      const d = dr * dr + dg * dg + db * db;
      if (d < menorDistancia) {
        menorDistancia = d;
        melhor = k;
      }
    }

    cache.set(chave, melhor);
    saida[p] = melhor;
  }

  return saida;
}

/* ── LZW ────────────────────────────────────────────────────────────────── */

/**
 * A compressão do GIF.
 *
 * O tamanho do código cresce junto com a tabela, e é aí que mora o erro
 * clássico: o decodificador monta a tabela um passo atrás do codificador, então
 * a troca de largura tem de acontecer no mesmo ponto dos dois lados — logo
 * depois de inserir o código de valor `1 << tamanho`. Errar por um deixa o
 * arquivo ilegível a partir da primeira troca, e é por isso que o teste desta
 * função decodifica de volta com um decodificador escrito do zero.
 */
export function comprimirLZW(indices: Uint8Array, tamanhoMinimo: number): Uint8Array {
  const codigoLimpeza = 1 << tamanhoMinimo;
  const codigoFim = codigoLimpeza + 1;

  let tamanhoCodigo = tamanhoMinimo + 1;
  let proximoCodigo = codigoFim + 1;
  let dicionario = new Map<number, number>();

  const saida: number[] = [];
  let acumulador = 0;
  let bitsNoAcumulador = 0;

  const emitir = (codigo: number) => {
    acumulador |= codigo << bitsNoAcumulador;
    bitsNoAcumulador += tamanhoCodigo;
    while (bitsNoAcumulador >= 8) {
      saida.push(acumulador & 0xff);
      acumulador >>= 8;
      bitsNoAcumulador -= 8;
    }
  };

  emitir(codigoLimpeza);

  if (indices.length === 0) {
    emitir(codigoFim);
    if (bitsNoAcumulador > 0) saida.push(acumulador & 0xff);
    return Uint8Array.from(saida);
  }

  let prefixo = indices[0] ?? 0;

  for (let i = 1; i < indices.length; i += 1) {
    const atual = indices[i] ?? 0;
    // O prefixo cabe em 12 bits, então os dois viram uma chave numérica só.
    const chave = (prefixo << 8) | atual;

    const achado = dicionario.get(chave);
    if (achado !== undefined) {
      prefixo = achado;
      continue;
    }

    emitir(prefixo);
    dicionario.set(chave, proximoCodigo);
    proximoCodigo += 1;

    if (proximoCodigo === 4096) {
      // A tabela encheu: limpa dos dois lados e recomeça.
      emitir(codigoLimpeza);
      dicionario = new Map();
      tamanhoCodigo = tamanhoMinimo + 1;
      proximoCodigo = codigoFim + 1;
    } else if (proximoCodigo === (1 << tamanhoCodigo) + 1 && tamanhoCodigo < 12) {
      // O `+ 1` é o passo de atraso: quando o codificador insere o código de
      // valor `1 << tamanho`, o decodificador ainda não inseriu o dele — ele
      // monta a tabela uma entrada atrás. Trocar a largura junto com a própria
      // tabela deixaria os dois lados lendo bits diferentes a partir daqui.
      tamanhoCodigo += 1;
    }

    prefixo = atual;
  }

  emitir(prefixo);
  emitir(codigoFim);
  if (bitsNoAcumulador > 0) saida.push(acumulador & 0xff);

  return Uint8Array.from(saida);
}

/* ── Montagem do arquivo ────────────────────────────────────────────────── */

export interface OpcoesGif {
  readonly largura: number;
  readonly altura: number;
  readonly paleta: readonly Cor[];
  readonly quadros: readonly QuadroGif[];
}

function bitsParaCores(quantidade: number): number {
  let bits = 1;
  while (1 << bits < quantidade) bits += 1;
  return Math.min(8, Math.max(1, bits));
}

export function codificarGif(opcoes: OpcoesGif): Uint8Array {
  const { largura, altura, paleta, quadros } = opcoes;

  const bits = bitsParaCores(paleta.length);
  const coresNaTabela = 1 << bits;

  const bytes: number[] = [];
  const put = (...b: readonly number[]) => bytes.push(...b);
  const put16 = (n: number) => bytes.push(n & 0xff, (n >> 8) & 0xff);

  // Cabeçalho e descritor de tela.
  for (const c of 'GIF89a') put(c.charCodeAt(0));
  put16(largura);
  put16(altura);
  // Tabela global presente, resolução de cor máxima, tamanho da tabela.
  put(0x80 | 0x70 | (bits - 1), 0, 0);

  for (let i = 0; i < coresNaTabela; i += 1) {
    const cor = paleta[i] ?? { r: 0, g: 0, b: 0 };
    put(cor.r, cor.g, cor.b);
  }

  // Extensão da Netscape: é o que faz o GIF repetir para sempre.
  put(0x21, 0xff, 0x0b);
  for (const c of 'NETSCAPE2.0') put(c.charCodeAt(0));
  put(0x03, 0x01, 0x00, 0x00, 0x00);

  for (const quadro of quadros) {
    // Controle gráfico: descarte 1 (deixar como está), sem transparência.
    put(0x21, 0xf9, 0x04, 0x04);
    put16(quadro.atrasoCs);
    put(0x00, 0x00);

    // Descritor da imagem — o retângulo que este quadro repinta.
    put(0x2c);
    put16(quadro.esquerda);
    put16(quadro.topo);
    put16(quadro.largura);
    put16(quadro.altura);
    put(0x00);

    const tamanhoMinimo = Math.max(2, bits);
    put(tamanhoMinimo);

    const comprimido = comprimirLZW(quadro.indices, tamanhoMinimo);
    // Os dados vão em sub-blocos de no máximo 255 bytes, cada um precedido do
    // próprio tamanho; o bloco vazio fecha a sequência.
    for (let i = 0; i < comprimido.length; i += 255) {
      const pedaco = comprimido.subarray(i, Math.min(i + 255, comprimido.length));
      put(pedaco.length);
      for (const b of pedaco) put(b);
    }
    put(0x00);
  }

  put(0x3b);
  return Uint8Array.from(bytes);
}
